import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Maximum number of monthly snapshots to retain (24 months = 2 years)
const MAX_HISTORY_MONTHS = 24;

export interface MonthlyFinancialSnapshot {
  date: { month: number; year: number };
  revenue: number;
  costs: number;
  profit: number;
  cash: number;
}

export interface FinancialHistoryState {
  monthlySnapshots: MonthlyFinancialSnapshot[];
}

interface FinancialHistoryStore extends FinancialHistoryState {
  // Actions
  recordMonthlySnapshot: (snapshot: MonthlyFinancialSnapshot) => void;
  getMonthlyHistory: (months?: number) => MonthlyFinancialSnapshot[];

  // Hydration from save
  hydrateFromSave: (saveData: FinancialHistoryState | undefined) => void;

  // Export for save
  exportForSave: () => FinancialHistoryState;

  // Reset
  reset: () => void;
}

const initialState: FinancialHistoryState = {
  monthlySnapshots: []
};

export const useFinancialHistoryStore = create<FinancialHistoryStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      ...initialState,

      // Record a new monthly snapshot
      recordMonthlySnapshot: (snapshot) => {
        // Validate inputs - ignore invalid data
        // Note: profit can be negative (losses), so we don't validate it
        // Only revenue, costs, and cash must be non-negative
        if (snapshot.revenue < 0 || snapshot.costs < 0 || snapshot.cash < 0) {
          if (import.meta.env.DEV) {
            console.warn('[FinancialHistoryStore] Invalid snapshot rejected:', snapshot);
          }
          return;
        }

        set(state => {
          // Check for duplicate month (same month/year already exists)
          const existingIndex = state.monthlySnapshots.findIndex(
            s => s.date.month === snapshot.date.month && s.date.year === snapshot.date.year
          );

          let updatedSnapshots: MonthlyFinancialSnapshot[];

          if (existingIndex >= 0) {
            // Replace existing snapshot for this month
            updatedSnapshots = [...state.monthlySnapshots];
            updatedSnapshots[existingIndex] = snapshot;
          } else {
            // Add new snapshot and trim to max size
            updatedSnapshots = [...state.monthlySnapshots, snapshot];
            if (updatedSnapshots.length > MAX_HISTORY_MONTHS) {
              updatedSnapshots.shift(); // Remove oldest snapshot
            }
          }

          return { monthlySnapshots: updatedSnapshots };
        }, false, 'recordMonthlySnapshot');
      },

      // Get history, optionally limited to N most recent months
      getMonthlyHistory: (months) => {
        const snapshots = get().monthlySnapshots;
        if (months && months < snapshots.length) {
          return snapshots.slice(-months);
        }
        return snapshots;
      },

      // Hydrate from save data
      hydrateFromSave: (saveData) => {
        if (!saveData?.monthlySnapshots) {
          return;
        }

        set({
          monthlySnapshots: saveData.monthlySnapshots
        }, false, 'hydrateFromSave');
      },

      // Export for save
      exportForSave: () => {
        return {
          monthlySnapshots: get().monthlySnapshots
        };
      },

      // Reset to initial state
      reset: () => {
        set(initialState, false, 'reset');
      }
    }),
    { name: 'FinancialHistoryStore' }
  )
);
